import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { AlertCircle, CalendarX, Clock, Loader2 } from 'lucide-react'
import type { RootState } from '@/redux'
import { Button } from '@/components/ui/button'
import type {
  DiscoveredFilterValue,
  FilterDiscoveryStatus,
} from '@/types/DashboardFilterDiscovery.types'

/**
 * FiltersBlock.primitives — presentational + discovery wiring extracted from
 * the prior monolithic FiltersBlock.tsx. Slice 4 of EPIC-132's metadata
 * refactor (TDD §7.5) factors these out so the new generic FilterRow
 * dispatcher can reuse them without duplicating chrome.
 *
 * Visual chrome (Tailwind classes, ARIA wiring, mockup-faithful copy) is
 * preserved verbatim from the prior implementation. No mockup change.
 */

// ─── DefaultBadge ─────────────────────────────────────────────────────────────

/** "Default" badge — green pill signalling a credential- or static-default value. */
export function DefaultBadge({ label = 'Default' }: { label?: string }) {
  return (
    <span className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded-[10px] bg-[#F0FDF4] text-[#15803D] border border-[#BBF7D0]">
      {label}
    </span>
  )
}

// ─── SyncNote ─────────────────────────────────────────────────────────────────

/** Sync-on-refresh info note shown at the top of every filter card. */
export function SyncNote() {
  return (
    <div
      className="px-2.5 py-2 bg-accent-background border border-[#BFDBFE] rounded-[7px] text-xs text-accent leading-relaxed"
      role="note"
    >
      Dashboard refreshes with new records since your last sync - use{' '}
      <strong>Reset</strong> (in the dashboard menu) to start fresh.
    </div>
  )
}

// ─── DomainCard ───────────────────────────────────────────────────────────────

interface DomainCardProps {
  ariaLabel: string
  children: React.ReactNode
}

export function DomainCard({ ariaLabel, children }: DomainCardProps) {
  return (
    <div
      className="bg-card border-[1.5px] border-border rounded-[12px] px-4 py-3.5 max-w-[600px]"
      role="region"
      aria-label={ariaLabel}
    >
      <div className="mb-3.5">
        <SyncNote />
      </div>
      {children}
    </div>
  )
}

// ─── SkeletonRow ──────────────────────────────────────────────────────────────

/**
 * Per-filter loading skeleton. Mockup
 * `2026-04-28-filter-enrollment-loading-v1.html` keeps the filter card chrome
 * visible while a spinner + label anchor the loading context.
 */
export function SkeletonRow({
  label,
  variant = 'pills',
}: {
  label: string
  variant?: 'pills' | 'rows'
}) {
  return (
    <div className="mb-4" aria-busy="true" aria-label={`Loading ${label}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm font-semibold text-primary">{label}</span>
        <Loader2
          size={12}
          className="text-muted-foreground animate-spin"
          aria-hidden="true"
        />
      </div>
      {variant === 'pills' ? (
        <div className="flex flex-wrap gap-1.5">
          <div className="h-7 w-14 rounded-full bg-muted animate-pulse" />
          <div className="h-7 w-14 rounded-full bg-muted animate-pulse" />
          <div className="h-7 w-14 rounded-full bg-muted animate-pulse" />
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          <div className="h-8 w-full rounded-md bg-muted animate-pulse" />
          <div className="h-8 w-full rounded-md bg-muted animate-pulse" />
          <div className="h-8 w-[88%] rounded-md bg-muted animate-pulse" />
        </div>
      )}
    </div>
  )
}

// ─── FilterErrorWell ──────────────────────────────────────────────────────────

/**
 * FilterErrorWell — generic discovery error per mockup
 * `2026-04-28-filter-enrollment-error-v1.html`. Try-again re-fires the
 * discovery thunk. Generic copy — no raw API errors leak to the user.
 */
export function FilterErrorWell({ onRetry }: { onRetry: () => void }) {
  return (
    <div
      role="alert"
      aria-live="assertive"
      className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-3.5 flex flex-col gap-2"
    >
      <div className="flex items-center gap-2.5">
        <AlertCircle size={18} className="text-destructive flex-shrink-0" aria-hidden="true" />
        <span className="text-sm font-semibold text-primary">
          Couldn&apos;t load filter options
        </span>
      </div>
      <p className="text-xs text-secondary leading-relaxed">
        We had trouble fetching your CampMinder data. This is usually a temporary
        issue — try again in a moment.
      </p>
      <div className="flex items-center gap-2.5 mt-1">
        <Button
          type="button"
          size="sm"
          className="h-8"
          aria-label="Try fetching filter options again"
          onClick={onRetry}
        >
          Try again
        </Button>
      </div>
    </div>
  )
}

// ─── RateLimitedWell ──────────────────────────────────────────────────────────

/**
 * RateLimitedWell — Bug #3 (2026-05-14). Surfaces a CampMinder rate-limit
 * back to the user with a live countdown so the user understands we're
 * waiting and not stuck. Auto-fires `onRetry` once the countdown elapses
 * so the user doesn't have to click anything.
 *
 * Distinct from `FilterErrorWell` (generic retry) — different copy and a
 * different mental model. Pre-fix, all SDK rate-limit errors were collapsed
 * into the generic upstream-failure path and the user saw "Try again" with
 * no indication that the issue was vendor-driven and self-resolving.
 */
export interface RateLimitedWellProps {
  retryAfterSeconds: number
  onRetry: () => void
}

export function RateLimitedWell({
  retryAfterSeconds,
  onRetry,
}: RateLimitedWellProps) {
  const [secondsLeft, setSecondsLeft] = useState(retryAfterSeconds)
  // Round-1 review (2026-05-21, Qodo Q5 + Claude #1): the parent passes
  // `onRetry` as an inline closure, so its identity changes every render.
  // Without these refs the countdown effect would re-run on every parent
  // re-render (clearing and restarting the 1s setTimeout, stalling the
  // countdown), and once `secondsLeft <= 0` could fire `onRetry()` more
  // than once before the well unmounts. Stash `onRetry` in a ref; drop it
  // from the dep array; gate the elapsed-fire with `hasRetriedRef` and
  // reset it whenever a fresh rate-limit window starts.
  const onRetryRef = useRef(onRetry)
  useEffect(() => {
    onRetryRef.current = onRetry
  }, [onRetry])
  const hasRetriedRef = useRef(false)

  // Reset the countdown if a fresh rate-limit fires with a different window
  // (e.g. the auto-retry hit the rate limit again with a new retry-after).
  useEffect(() => {
    setSecondsLeft(retryAfterSeconds)
    hasRetriedRef.current = false
  }, [retryAfterSeconds])

  useEffect(() => {
    if (secondsLeft <= 0) {
      if (!hasRetriedRef.current) {
        hasRetriedRef.current = true
        onRetryRef.current()
      }
      return
    }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000)
    return () => clearTimeout(t)
    // onRetry is intentionally not in the dep array — accessed via onRetryRef
    // so parent re-renders don't restart the 1s timeout (Q5/Claude #1).
  }, [secondsLeft])

  return (
    <div
      role="status"
      aria-live="polite"
      className="bg-[#FEF3C7] border border-[#FCD34D] rounded-lg p-3.5 flex flex-col gap-2"
      data-testid="filter-rate-limited-well"
    >
      <div className="flex items-center gap-2.5">
        <Clock
          size={18}
          className="text-[#92400E] flex-shrink-0"
          aria-hidden="true"
        />
        <span className="text-sm font-semibold text-primary">
          CampMinder asked us to wait a moment
        </span>
      </div>
      <p className="text-xs text-secondary leading-relaxed">
        CampMinder is rate-limiting requests right now.{' '}
        {secondsLeft > 0 ? (
          <>
            Resuming automatically in{' '}
            <span className="font-semibold text-primary">{secondsLeft}s</span> —
            no need to click anything.
          </>
        ) : (
          <>Retrying now…</>
        )}
      </p>
    </div>
  )
}

// ─── CredentialExpiredWell ────────────────────────────────────────────────────

/**
 * Credential-invalid path. The user has to re-harvest the credential before
 * discovery can proceed.
 */
export function CredentialExpiredWell() {
  return (
    <div
      role="alert"
      aria-live="assertive"
      className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-3.5 flex flex-col gap-2"
    >
      <div className="flex items-center gap-2.5">
        <AlertCircle size={18} className="text-destructive flex-shrink-0" aria-hidden="true" />
        <span className="text-sm font-semibold text-primary">
          Credential is no longer valid
        </span>
      </div>
      <p className="text-xs text-secondary leading-relaxed">
        We couldn&apos;t authenticate to CampMinder with this credential. The API
        key may have been deleted, revoked, or expired. Check your CampMinder
        account (API Access Admin) to confirm the credential still exists, then
        re-connect or harvest a new one.
      </p>
      <div className="flex items-center gap-2.5 mt-1">
        <Link
          to="/credentials"
          className="inline-flex items-center justify-center h-8 px-4 rounded-md bg-accent text-white text-xs font-semibold hover:bg-accent/90 focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-2"
          aria-label="Go to the Credentials page to re-connect or harvest a new credential"
        >
          Go to Credentials
        </Link>
      </div>
    </div>
  )
}

// ─── ZeroSeasonsBlock ─────────────────────────────────────────────────────────

/**
 * Hard-blocker for the `season` filter that returned an empty choices array.
 * Mockup `2026-04-28-filter-enrollment-zero-seasons-v1.html`. Confirm Filters
 * CTA is hidden by the parent when this state is active.
 */
export function ZeroSeasonsBlock({ onCheckAgain }: { onCheckAgain: () => void }) {
  return (
    <div
      role="alert"
      aria-live="assertive"
      aria-labelledby="zero-seasons-heading"
      className="flex flex-col items-center text-center px-4 py-6"
    >
      <CalendarX
        size={48}
        className="text-muted-foreground mb-3"
        aria-hidden="true"
      />
      <h2
        id="zero-seasons-heading"
        className="text-base font-bold text-primary mb-2"
      >
        No seasons found in CampMinder
      </h2>
      <p className="text-sm text-secondary leading-relaxed max-w-[380px] mb-4">
        Your CampMinder account has no seasons configured. Set up at least one
        season in CampMinder, then come back to continue building your
        dashboard.
      </p>
      <div className="flex flex-col items-stretch gap-2 w-full max-w-[260px]">
        <Link
          to="/credentials"
          className="inline-flex items-center justify-center h-10 px-4 rounded-md bg-accent text-white text-sm font-semibold hover:bg-accent/90 focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-2"
          aria-label="Go to Credentials page to check your CampMinder connection"
        >
          Go to Credentials
        </Link>
        <button
          type="button"
          className="inline-flex items-center justify-center h-9 px-4 rounded-md border-[1.5px] border-border bg-card text-sm font-medium text-secondary hover:border-accent hover:text-accent hover:bg-accent-background focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-2"
          aria-label="Check CampMinder again for seasons"
          onClick={onCheckAgain}
        >
          Check again
        </button>
      </div>
    </div>
  )
}

// ─── PillToggle (multi-select) ────────────────────────────────────────────────

interface PillToggleProps {
  label: string
  selected: boolean
  isCurrent?: boolean
  onToggle: () => void
  ariaLabelOverride?: string
  size?: 'season' | 'status'
}

export function PillToggle({
  label,
  selected,
  isCurrent,
  onToggle,
  ariaLabelOverride,
  size = 'status',
}: PillToggleProps) {
  const sizeCls =
    size === 'season' ? 'px-3 py-1 text-sm' : 'px-2.5 py-1 text-xs'
  const fontWeightCls = isCurrent
    ? 'font-bold'
    : selected
    ? 'font-semibold'
    : 'font-medium'
  const colorCls = selected
    ? 'border-accent bg-accent-background text-accent'
    : 'border-border bg-card text-secondary hover:border-accent hover:bg-accent-background'
  const ariaLabel =
    ariaLabelOverride ??
    `${label}${selected ? ' — selected' : ''}${isCurrent ? ', current season' : ''}`

  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={ariaLabel}
      onClick={onToggle}
      className={`rounded-full border-[1.5px] ${sizeCls} ${fontWeightCls} ${colorCls} transition-all duration-150 focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-2`}
    >
      {label}
    </button>
  )
}

// ─── SinglePillToggle (single-select) ─────────────────────────────────────────

interface SinglePillToggleProps {
  label: string
  selected: boolean
  onSelect: () => void
}

export function SinglePillToggle({
  label,
  selected,
  onSelect,
}: SinglePillToggleProps) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={`${label}${selected ? ' — selected' : ''}`}
      onClick={onSelect}
      className={`px-3 py-1 rounded-full border-[1.5px] text-sm transition-all duration-150 focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-2
        ${
          selected
            ? 'border-accent bg-accent-background text-accent font-semibold'
            : 'border-border bg-card text-secondary font-medium hover:border-accent hover:bg-accent-background'
        }`}
    >
      {label}
    </button>
  )
}

// ─── GroupedCheckboxList ──────────────────────────────────────────────────────

interface GroupedCheckboxListProps {
  ariaLabel: string
  helperNote: string
  values: DiscoveredFilterValue[]
  selected: string[]
  onToggle: (value: string) => void
}

export function GroupedCheckboxList({
  ariaLabel,
  helperNote,
  values,
  selected,
  onToggle,
}: GroupedCheckboxListProps) {
  const grouped = useMemo(() => {
    const map = new Map<string, DiscoveredFilterValue[]>()
    for (const v of values) {
      const key = v.groupLabel ?? ''
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(v)
    }
    for (const [k, arr] of map) {
      arr.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      map.set(k, arr)
    }
    return Array.from(map.entries())
  }, [values])

  return (
    <div>
      <p
        className="text-xs text-secondary italic mb-2 px-2.5 py-1.5 bg-background rounded-md border border-border"
        role="note"
      >
        {helperNote}
      </p>
      <div role="group" aria-label={ariaLabel} className="flex flex-col gap-2.5">
        {grouped.map(([groupLabel, groupValues]) => (
          <div key={groupLabel || '__ungrouped__'}>
            {groupLabel !== '' && (
              <div
                className="text-[10.5px] font-bold text-secondary uppercase tracking-wide mb-1.5 px-0.5"
                aria-hidden="true"
              >
                {groupLabel}
              </div>
            )}
            <div className="flex flex-col gap-1">
              {groupValues.map(v => {
                const isChecked = selected.includes(v.value)
                return (
                  <label
                    key={v.value}
                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md border cursor-pointer transition-colors
                      ${
                        isChecked
                          ? 'border-accent bg-accent-background'
                          : 'border-border bg-card hover:border-accent hover:bg-accent-background'
                      }`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => onToggle(v.value)}
                      className="w-3.5 h-3.5 accent-accent flex-shrink-0"
                      aria-label={v.displayName}
                    />
                    <span className="text-sm text-primary font-medium">{v.displayName}</span>
                  </label>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── DiscoveryFilterRow ───────────────────────────────────────────────────────

interface DiscoveryFilterRowProps {
  /** Unqualified picker key (e.g. 'enrollment'). For Redux state-key usage only. */
  domainKey: string
  filterCode: string
  label: string
  /**
   * Qualified dashboard-era domain id from the registry. When null, the
   * registry has not loaded — render the loading skeleton.
   */
  qualifiedDomainId: string | null
  /** True for the season filter — empty result triggers blocking-zero-seasons. */
  isSeasonFilter?: boolean
  /** Render variant when status === 'ready'. */
  renderReady: (choices: DiscoveredFilterValue[]) => React.ReactNode
  loadingVariant?: 'pills' | 'rows'
  /** Re-fire discovery (parent injects the parentContext closure). */
  onRetry: () => void
  /** Re-fire discovery — used by the zero-seasons "Check again" CTA. */
  onCheckAgain: () => void
  /** Bubbles up "this filter is currently blocking" so parent can hide Confirm. */
  onBlockingChange?: (blocking: boolean) => void
}

export function DiscoveryFilterRow({
  filterCode,
  label,
  qualifiedDomainId,
  isSeasonFilter,
  renderReady,
  loadingVariant = 'pills',
  onRetry,
  onCheckAgain,
  onBlockingChange,
}: DiscoveryFilterRowProps) {
  const stateKey = qualifiedDomainId
    ? `${qualifiedDomainId}.${filterCode}`
    : null
  const status: FilterDiscoveryStatus = useSelector((state: RootState) =>
    stateKey
      ? state.dashboardCreation.domainFilterStatus[stateKey] ?? 'idle'
      : 'idle',
  )
  const choices = useSelector((state: RootState) =>
    stateKey
      ? state.dashboardCreation.domainFilterChoices[stateKey] ?? null
      : null,
  )
  // Bug #3 (2026-05-14) — paired with status === 'rate_limited' for the
  // countdown UX. Undefined when not rate-limited.
  const rateLimitRetryAfter = useSelector((state: RootState) =>
    stateKey
      ? state.dashboardCreation.domainFilterRateLimitRetryAfter[stateKey]
      : undefined,
  )

  const blocking =
    isSeasonFilter === true &&
    status === 'ready' &&
    choices !== null &&
    choices.length === 0

  useEffect(() => {
    onBlockingChange?.(blocking)
  }, [blocking, onBlockingChange])

  if (status === 'idle' || status === 'loading') {
    return <SkeletonRow label={label} variant={loadingVariant} />
  }

  if (status === 'credential_expired') {
    return (
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-sm font-semibold text-primary">{label}</span>
        </div>
        <CredentialExpiredWell />
      </div>
    )
  }

  if (status === 'rate_limited') {
    return (
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-sm font-semibold text-primary">{label}</span>
        </div>
        <RateLimitedWell
          retryAfterSeconds={rateLimitRetryAfter ?? 5}
          onRetry={onRetry}
        />
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-sm font-semibold text-primary">{label}</span>
        </div>
        <FilterErrorWell onRetry={onRetry} />
      </div>
    )
  }

  // status === 'ready'
  const ready = choices ?? []

  if (blocking) {
    return <ZeroSeasonsBlock onCheckAgain={onCheckAgain} />
  }

  return <>{renderReady(ready)}</>
}
